import { count } from 'pages/admin/shared/formatters';
import type { QueueStatus } from './types';

/** Each status's label and Carbon Tag colour. The label carries the meaning; the colour only echoes it. */
export const QUEUE_STATUS_TAGS: Record<
  QueueStatus,
  { label: string; type: 'green' | 'warm-gray' | 'red' | 'gray' | 'magenta' }
> = {
  ok: { label: 'OK', type: 'green' },
  warning: { label: 'Warning', type: 'warm-gray' },
  breaching: { label: 'Breaching', type: 'red' },
  no_sla: { label: 'No SLA', type: 'gray' },
  stranded: { label: 'Stranded', type: 'magenta' },
};

/**
 * A whole number of seconds as its largest even unit: 5s, 5m, 5h.
 *
 * @param {number} seconds - The duration.
 * @return {string} The short form.
 */
export const shortDuration = (seconds: number): string => {
  if (seconds >= 3_600 && seconds % 3_600 === 0) return `${seconds / 3_600}h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
};

/**
 * A queue's latency against its SLA budget, as text: "40% of 5m budget", or
 * "No SLA" for a lane without one.
 *
 * @param {number} latencySeconds - The age of the oldest job.
 * @param {number | null} budgetSeconds - The SLA budget, or null.
 * @return {string} The readout.
 */
export const budgetReadout = (
  latencySeconds: number,
  budgetSeconds: number | null
): string => {
  if (budgetSeconds === null) return 'No SLA';
  const percent = Math.round((latencySeconds / budgetSeconds) * 100);
  return `${count(percent)}% of ${shortDuration(budgetSeconds)} budget`;
};

/**
 * The tier queues, in Sidekiq::TimedQueueInspector::TIER_QUEUES order. The
 * page takes the list from the polled summary; this is the fallback when the
 * summary has not loaded or failed, so a queue scan still works then.
 * spec/models/sidekiq/timed_queue_inspector_spec.rb fails if this list stops
 * matching TIER_QUEUES, or if it moves out of this file.
 */
export const TIER_QUEUE_NAMES = [
  'within_5_seconds',
  'within_5_minutes',
  'within_5_minutes_burst',
  'within_5_hours',
  'within_5_hours_burst',
  'compute',
];
